import { ColDef } from "ag-grid-community";
import { isArray } from "highcharts";
import {
  BoardIds,
  FilterIdentifier,
  FilterUserSelection,
} from "../../../common/generic/generic-filters-boards-attributes";
import { BuildUrlForHyperlinkInGrid } from "../../../common/generic/generic-helper";
import { SCAttributes } from "../../../common/generic/generic-sc-attributes";
import { SCKpis } from "../../../common/generic/generic-sc-domain";
import {
  IGenericAggregateResponse,
  IGenericHyperlinkFilter,
} from "../../../common/generic/generic-types";
import { SCProfileDictionary } from "../../../common/salescredit/salescredit-profile-dictionary";
import { Functions } from "../../../common/utils/clientlistHelper";
import {
  getAttributesColumnDefs,
  getKpisColumnDefs,
  GridHeader,
} from "./headers";
import { AgGridUserPreference } from "../../../common/sg-dashboard/userPreference/userPreference.typings";

export function getParams(
  columnsFilter: string[],
  userPreference?: AgGridUserPreference
): [GridHeader[], string[], string[]] {
  const [columnsHeader, attributesSelected] = getAttributes(columnsFilter);

  const kpisHeader = getKpisHeaders(columnsFilter);
  const kpisSelected = computedKpis(kpisHeader.map(
    (kpi: ColDef) => kpi.field
  ) as string[]);

  const headerResult = applyUserPreference(
    columnsHeader,
    userPreference
  ).concat(applyUserPreference(kpisHeader, userPreference));
  return [headerResult, attributesSelected, kpisSelected];
}

export function getMapping(
  data: IGenericAggregateResponse,
  attributes: string[],
  kpis: string[]
): any[] {
  const profileDictionary = new SCProfileDictionary();
  if (!data.aggregates || data.aggregates.length === 0) {
    return [];
  }
  const rows: any[] = [];
  const dateForFilter = [];
  if (data.request) {
    dateForFilter.push(data.request.date);
  }

  for (const item of data.aggregates) {
    const row: { [field: string]: string | number } = {};

    if (
      attributes.indexOf(SCAttributes.SC_CLIENT_LEID) > -1 ||
      attributes.indexOf(SCAttributes.SC_CLIENT_LENAME) > -1
    ) {
      const clientId =
        item.dimensionAttributeValues[SCAttributes.SC_CLIENT_LEID];
      const clientIdForFilter = [];

      clientIdForFilter.push(clientId);
      const hFilterTypes: any = [];
      hFilterTypes.push({
        filter_id: FilterIdentifier.client_selector,
        id: FilterUserSelection.client_selector,
        value: clientIdForFilter,
      });
      hFilterTypes.push({
        filter_id: FilterIdentifier.asOfDate,
        id: FilterUserSelection.asOfDate,
        value: dateForFilter,
      });
      const hFilter: IGenericHyperlinkFilter = {
        board_id: BoardIds.Client_Snapshot,
        filter: hFilterTypes,
      };

      row.hlink = BuildUrlForHyperlinkInGrid(hFilter);
    }
    for (const attributeRaw of attributes) {
      row[attributeRaw] =
        item.dimensionAttributeValues[attributeRaw] === "Null"
          ? "--"
          : item.dimensionAttributeValues[attributeRaw];
    }

    for (const kpiRaw of kpis) {
      row[kpiRaw] = CheckKpiForMtdVar(
        kpiRaw,
        item.indicatorKpiValues[profileDictionary.GetKpi("", kpiRaw)]
      );
    }
    const scYtdVarYoy = "sc_ytd_var_yoy";
    row[scYtdVarYoy] = Functions.calculateVariation(
      item.indicatorKpiValues.sc_ytd,
      item.indicatorKpiValues.sc_ytd_1
    );
    rows.push(row);
  }
  return rows;
}

export function getSortByKpi(kpiSelected: string[]): string {
  if (!isArray(kpiSelected) || kpiSelected.length === 0) {
    return "";
  }
  if (kpiSelected.indexOf(SCKpis.SC_YTD) > -1) {
    return SCKpis.SC_YTD;
  }
  return kpiSelected[0];
}

function applyUserPreference(
  columnHeaders: GridHeader[],
  userPreferenceColumns?: AgGridUserPreference
) {
  if (
    !userPreferenceColumns ||
    !userPreferenceColumns.columns ||
    userPreferenceColumns.columns.length < 1
  ) {
    return columnHeaders;
  }

  const columnHeadersResult: GridHeader[] = [];

  userPreferenceColumns.columns.forEach(elem => {
    if (
      columnHeaders.filter((x: ColDef) => x.field === elem.colId).length > 0
    ) {
      const header = columnHeaders.filter(
        (x: any) => x.field === elem.colId
      )[0] as ColDef;

      columnHeadersResult.push({
        ...header,
        pinned: elem.pinned,
        width: elem.width,
      });
    }
  });

  columnHeaders.forEach((elem: ColDef) => {
    if (
      columnHeadersResult.filter((x: ColDef) => x.field === elem.field).length <
      1
    ) {
      columnHeadersResult.push(elem);
    }
  });

  return columnHeadersResult;
}

function getAttributes(columnsFilter: string[]): [GridHeader[], string[]] {
  const attrHeaders = getAttributesColumnDefs();

  const columnsHeader: GridHeader[] = [];
  const attributesSelected: string[] = [];
  attrHeaders.forEach(attr => {
    if (columnsFilter.indexOf(attr.headerName) > -1) {
      columnsHeader.push(attr);
      attributesSelected.push(attr.field);
      if (
        attr.field === SCAttributes.SC_CLIENT_LENAME &&
        columnsFilter.indexOf("Final Com LE Id") < 0
      ) {
        attributesSelected.push(SCAttributes.SC_CLIENT_LEID);
      }
    }
  });

  return [columnsHeader, attributesSelected];
}

function getKpisHeaders(columnsFilter: string[]): GridHeader[] {
  const columnsHeader: GridHeader[] = [];
  const kpiHeaders = getKpisColumnDefs();
  kpiHeaders.forEach(kpi => {
    if (columnsFilter.indexOf(kpi.headerName) > -1) {
      columnsHeader.push(kpi as GridHeader);
    }
  });
  return columnsHeader;
}

function computedKpis(kpisSelected: string[]) {
  const index = kpisSelected.indexOf("sc_ytd_var_yoy");
  if (index > -1) {
    kpisSelected.splice(index, 1);
    if (kpisSelected.indexOf(SCKpis.SC_YTD) < 0) {
      kpisSelected.push(SCKpis.SC_YTD);
    }
    if (kpisSelected.indexOf(SCKpis.SC_YTD_1) < 0) {
      kpisSelected.push(SCKpis.SC_YTD_1);
    }
  }
  return kpisSelected;
}

function CheckKpiForMtdVar(kpiRaw: string, value: number): number {
  return kpiRaw !== "sc_mtd_yoy"
    ? Functions.formatMoney(value)
    : isNaN(value)
    ? 0
    : value;
}
